import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { isLoading, useStatus } from 'resift';
import usePrevious from 'helpers/usePrevious';
import { useSnackbar } from 'notistack';
import MappingProps from 'types/Mapping';

// Components
import MappingsLayout from './MappingsLayout';

// Actions
import makeBatchMappingsFetch from 'store/mappings/actions/batchMappingsFetch';
import makeGetSourceMappings from 'store/sources/actions/makeGetSourceMappings';
import makeGetMappingsAttributes from 'fetches/makeGetMappingsAttributes';
import SourceType from 'types/Source';

interface Props {
  source: SourceType;
  onSyncMappings?: () => void;
  sourceFile?: any;
}

function Container({ source, onSyncMappings, sourceFile }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const batchMappingsFetch = makeBatchMappingsFetch();
  const sourceMappingsFetch = makeGetSourceMappings(source.id);
  const getMappingsAttributes = makeGetMappingsAttributes(source.id);

  const status = useStatus(batchMappingsFetch);
  const prevStatus = usePrevious(status);

  const sendBatchMappingsDispatch = useCallback(async (newMappings: MappingProps[]) => {
    await dispatch(batchMappingsFetch(newMappings));
    dispatch(getMappingsAttributes());
  }, []);

  useEffect(() => {
    if (isLoading(prevStatus) && status === 1) {
      enqueueSnackbar('Saving mappings...');
      dispatch(sourceMappingsFetch());
    }

    if (isLoading(prevStatus) && status === 32) {
      enqueueSnackbar('There was an error submitting your changes. Please try again.', {
        variant: 'error',
      });
    }
  }, [status]);

  return (
    <MappingsLayout
      sendBatchMappings={sendBatchMappingsDispatch}
      source={source}
      onSyncMappings={onSyncMappings}
      sourceFile={sourceFile}
    />
  );
}

export default Container;
